import React, { useCallback, useEffect, useState } from 'react'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Radio from '@groovehq/internal-design-system/lib/components/Radio/Radio'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import { selectCurrentFoldersById } from 'ducks/folders/selectors/folders'
import {
  doUpdateSingleFolder,
  doCreateSingleFolder,
} from 'ducks/folders/operations/folders'
import { getRawId } from 'util/globalId'
import { styles } from './styles'
import UserSelection from '../../UserSelection'
import MailboxSelection from '../../MailboxSelection'
import FolderConditions from './FolderConditions'
import StateDropdown from './StateDropdown'

// keys should preferably match the object being stored in the entity store
// that way for updates, you can just spread the entity in reset() and not have to set each property individually
const FORM_SCHEMA = yup.object().shape({
  id: yup.string().nullable(),
  name: yup.string().required('Rule name is required'),
  conditions: yup
    .array()
    .min(1, 'Need at least one condition')
    .of(
      yup.object().shape({
        param: yup.string(),
        operator: yup.string(),
        value: yup.string().nullable(),
      })
    ),
})

export default function FolderEditDrawer({
  open,
  onClose,
  drawerResourceId,
  ...rest
}) {
  const dispatch = useDispatch()

  // no clue
  const isUpdate = drawerResourceId !== 'new'
  const isLoading = false
  const hasError = false
  const notFound = false

  // selectors
  const foldersById = useSelector(selectCurrentFoldersById)
  const folder = foldersById[drawerResourceId]

  const [loaded, setLoaded] = useState(false)

  const pair = useState(new Date().getTime())
  const setTimestamp = pair[1]

  // form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    control,
    setValue,
    getValues,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: {
      // defaultValue has to match yup form schema
      id: null,
      name: null,
      state: 'ACTIVE',
      displayCountWhenInactive: true,
      viewAccess: 'all-agents',
      channelVisibility: 'all',
      matchType: 'ANY',
      agentIds: [],
      groupIds: [],
      channelIds: [],
      conditions: [
        {
          operator: null,
          param: null,
          value: '',
        },
      ],
    },
  })

  const onToggleHideCount = useCallback(
    value => {
      setValue('displayCountWhenInactive', !value.target.checked)
      setTimestamp(new Date().getTime())
    },
    [setValue, setTimestamp]
  )

  const onUserRangeChange = useCallback(
    newRange => {
      setValue('viewAccess', newRange)
      setTimestamp(new Date().getTime())
    },
    [setValue, setTimestamp]
  )

  const onUserSelectedIdsChange = useCallback(
    selectedIds => {
      setValue('agentIds', selectedIds)
      setTimestamp(new Date().getTime())
    },
    [setValue, setTimestamp]
  )

  const onGroupSelectedIdsChange = useCallback(
    selectedIds => {
      setValue('groupIds', selectedIds)
      setTimestamp(new Date().getTime())
    },
    [setValue, setTimestamp]
  )

  const onChannelRangeChange = useCallback(
    newRange => {
      setValue('channelVisibility', newRange)
      setTimestamp(new Date().getTime())
    },
    [setValue, setTimestamp]
  )

  const onChannelSelectedIdsChange = useCallback(
    selectedIds => {
      setValue('channelIds', selectedIds)
      setTimestamp(new Date().getTime())
    },
    [setValue, setTimestamp]
  )

  const onMatchTypeChange = useCallback(
    ev => {
      const value = ev.target.value
      setValue('matchType', value)
      setTimestamp(new Date().getTime())
    },
    [setValue, setTimestamp]
  )

  const onSubmit = useCallback(
    data => {
      if (isUpdate) {
        dispatch(doUpdateSingleFolder(data))
      } else {
        dispatch(doCreateSingleFolder(data))
      }
      onClose()
    },
    [dispatch, onClose, isUpdate]
  )

  // Effects
  useEffect(
    () => {
      if (isUpdate && folder) {
        // load fields with template values
        const channelIds = Object.values(folder.channels).map(item => item.id)
        const agentIds = Object.values(folder.agents).map(item =>
          getRawId(item.id)
        )
        const groupIds = Object.values(folder.teams).map(item =>
          getRawId(item.id)
        )
        reset({
          // just a spread here cos the needed keys should match the form schema
          ...folder,
          channelIds,
          agentIds,
          groupIds,
        })

        setLoaded(true)
      }
    },
    // We intentially exclude dependancy props here to ensure tthat this
    // reset only triggers for the initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [folder, isUpdate]
  )

  if (isUpdate && !loaded) return 'Loading'

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Drawer
        {...rest}
        open={open}
        onClose={onClose}
        size="wide"
        title={isUpdate ? 'Edit Folder' : 'New Folder'}
        isLoading={isLoading}
        isError={hasError}
        isNoResultFound={notFound}
        footer={
          <ModalBtns
            saveBtnDisabled={!isValid}
            saveBtnText={isUpdate ? 'Save Changes' : 'Create a new Folder'}
            // type="submit" will automatically trigger the form submit event when clicked
            saveBtnHtmlType="submit"
          />
        }
      >
        <div className="grui pt-10">
          <div css={styles.topFields} className="grui mb-12">
            <Field
              {...register('name')}
              label="Template Name"
              placeholder="Enter template name..."
              name="name"
              validateStatus={errors?.name ? 'error' : undefined}
              help={errors?.name?.message}
            />
            <div css={styles.dropdownContainer}>
              <div css={fieldStyles.labelBox}>State</div>
              <StateDropdown control={control} />
            </div>
          </div>
        </div>

        <Checkbox
          id="hideCount"
          checked={!getValues('displayCountWhenInactive')}
          onChange={onToggleHideCount}
          className="grui mb-12"
        >
          Hide folder count on side navigation
        </Checkbox>

        <div className="grui mb-12">
          <div css={fieldStyles.labelBox}>Include</div>
          <Radio
            id="allConditions"
            name="conditions"
            className="grui mb-8"
            value="ALL"
            checked={getValues('matchType') === 'ALL'}
            onChange={onMatchTypeChange}
          >
            Conversation matches all conditions
          </Radio>
          <br />
          <Radio
            id="specificConditions"
            name="conditions"
            defaultChecked={getValues('matchType') === 'ANY'}
            value="ANY"
            onChange={onMatchTypeChange}
          >
            Conversation matches any conditions
          </Radio>
          <FolderConditions control={control} />
        </div>

        <div className="grui mb-12">
          <div css={fieldStyles.labelBox}>Accessible by</div>
          <UserSelection
            type={getValues('viewAccess')}
            agentIds={getValues('agentIds')}
            groupIds={getValues('groupIds')}
            onUserListChange={onUserSelectedIdsChange}
            onGroupListChange={onGroupSelectedIdsChange}
            onRangeTypeChange={onUserRangeChange}
            css={styles.selection}
            checkboxListStyles={styles.checkboxList}
          />
        </div>
        <div>
          <div css={fieldStyles.labelBox}>Visibility</div>
          <MailboxSelection
            selectedIds={getValues('channelIds')}
            type={getValues('channelVisibility')}
            onMailboxListChange={onChannelSelectedIdsChange}
            onMailboxTypeChange={onChannelRangeChange}
            css={styles.selection}
            checkboxListStyles={styles.checkboxList}
          />
        </div>
      </Drawer>
    </form>
  )
}
